import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SoecoButton } from "../../components/SoecoButton";
import { CompanyContext } from "../../context/CompanyContext";
import { useAddCompany, CreateCompany } from "../../hooks/companyHooks";
import { ErrorRecord } from "../../types";
import styled from "styled-components";
import { SoecoHeader } from "../../components/SoecoHeader";
import { useSnackBar } from "../../context/SnackbarContext";
import Div100vh from "react-div-100vh";
import { EmployeeContext } from "../../context/EmployeeContext";
import { getRolePriority } from "../../utils/util";
import AddIcon from "@mui/icons-material/Add";
import { isValidEmail } from "../../utils/util";
import AddEmailDialog from "../../components/AddEmailDialog";

type CreateCompanyForm = {
    name: string;
    canCreateSubcompany?: boolean;
    createdBy?: string;
    emailaddresses?: string[];
};

export const CreateCompanyPage = () => {
    const { company } = useContext(CompanyContext);
    const mutation = useAddCompany();
    const navigate = useNavigate();
    const snackBar = useSnackBar();
    const { employee } = useContext(EmployeeContext);
    const [form, setForm] = useState<CreateCompanyForm>({
        name: "",
        canCreateSubcompany: false,
        createdBy: employee.companyId,
        emailaddresses: [],
    });

    const [error, setError] = useState<boolean>(false);
    const [errors, setErrors] = useState<ErrorRecord<CreateCompanyForm>>({});
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [isAddEmailDialogOpen, setIsAddEmailDialogOpen] = useState(false);
    const [newEmail, setNewEmail] = useState("");

    const transformFormData = (form: CreateCompanyForm): CreateCompany => ({
        name: form.name,
        canCreateSubcompany: form.canCreateSubcompany,
        createdBy: form.createdBy,
        emailaddresses: form.emailaddresses,
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setDisableButton(false);
        setError(false);
    };

    const isValidForm = () => {
        let valid = true;
        const newErrors: ErrorRecord<CreateCompanyForm> = {};

        if (form.name.length < 1) {
            valid = false;
            newErrors.name = "Name cannot be empty";
        }
        if (!valid) {
            setErrors(newErrors);
            setError(true);
            return false;
        } else {
            setErrors({});
            setError(false);
            return true;
        }
    };
    const handleChangeEmails = (email: string) => {
        const existingEmailIndex = form.emailaddresses?.findIndex(
            existingEmail => existingEmail === email,
        );
        if (existingEmailIndex !== -1) {
            setForm({
                ...form,
                emailaddresses: form.emailaddresses?.filter(
                    (existingEmail, index) => index !== existingEmailIndex,
                ),
            });
        } else {
            setForm({
                ...form,
                emailaddresses: [...(form.emailaddresses || []), email],
            });
        }
        setDisableButton(false);
        setError(false);
    };
    const handleAddEmailClick = () => {
        setIsAddEmailDialogOpen(true);
    };

    const handleCloseAddEmailDialog = () => {
        setIsAddEmailDialogOpen(false);
        setNewEmail("");
    };

    const handleSaveNewEmail = () => {
        if (newEmail.trim() === "") return;
        setForm(prev => ({
            ...prev,
            emailaddresses: [...(prev.emailaddresses || []), newEmail.trim()],
        }));

        handleCloseAddEmailDialog();
    };

    const handleSubmit = () => {
        if (isValidForm()) {
            mutation.mutate(
                { company: transformFormData(form) },
                {
                    onError: error => {
                        if (error instanceof Error) {
                            snackBar.showSnackBar(error.message, "error");
                            setDisableButton(true);
                        }
                    },
                    onSuccess: () => {
                        snackBar.showSnackBar("Company created successfully", "success");
                        navigate(-1);
                    },
                },
            );
        }
    };

    return (
        <CreateCompanyPageWrapper>
            <SoecoHeader>Create Company</SoecoHeader>
            <CreateCompanyWrapper>
                <Form>
                    {company && (
                        <TextField
                            disabled={true}
                            variant="outlined"
                            defaultValue={company?.name}
                        />
                    )}
                    <TextField
                        name="name"
                        label="Name"
                        autoComplete="off"
                        error={error && errors.name ? true : false}
                        helperText={error && errors.name}
                        variant="outlined"
                        value={form.name}
                        onChange={handleChange}
                    />
                    {getRolePriority(employee.role) === 2 ||
                    getRolePriority(employee.role) === 3 ||
                    getRolePriority(employee.role) === 4 ? (
                        <></>
                    ) : (
                        <FormControlLabel
                            value={"end"}
                            label="Allow subcompany creation"
                            control={
                                <Checkbox
                                    checked={form.canCreateSubcompany}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            canCreateSubcompany: e.target.checked,
                                        }))
                                    }
                                />
                            }
                        />
                    )}
                    {getRolePriority(employee.role) === 0 && (
                        <>
                            <Divider />
                            <Typography>Email Recievers</Typography>
                            {form?.emailaddresses?.map((email: string) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            onChange={() => handleChangeEmails(email)}
                                            checked={form.emailaddresses?.includes(email) || false}
                                        />
                                    }
                                    label={email}
                                />
                            ))}
                            <AddEmailDialog
                                isAddEmailDialogOpen={isAddEmailDialogOpen}
                                handleCloseAddEmailDialog={handleCloseAddEmailDialog}
                                newEmail={newEmail}
                                setNewEmail={setNewEmail}
                                handleSaveNewEmail={handleSaveNewEmail}
                            />
                            <IconButton
                                onClick={handleAddEmailClick}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignSelf: "flex-start",
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </>
                    )}
                </Form>
            </CreateCompanyWrapper>
            <ActionButtons>
                <SoecoButton fullWidth size="large" color="secondary" onClick={() => navigate(-1)}>
                    Back
                </SoecoButton>
                <SoecoButton
                    fullWidth
                    size="large"
                    disabled={mutation.isPending || disableButton}
                    color="primary"
                    className="btnLoginCreate"
                    onClick={handleSubmit}
                >
                    Create
                </SoecoButton>
            </ActionButtons>
        </CreateCompanyPageWrapper>
    );
};

const CreateCompanyPageWrapper = styled(Div100vh)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
`;

const CreateCompanyWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 95%;
    height: 70%;
    align-content: center;
    gap: 2rem;
    overflow: scroll;
    padding-top: 10px;
`;

const Form = styled(FormControl)`
    display: flex;
    flex-direction: column;
    align-content: center;
    width: 95%;
    gap: 1rem;
`;

const ActionButtons = styled.div`
    display: flex;
    width: 90%;
    gap: 2rem;
    position: fixed;
    bottom: 2rem;
`;
